export type WallboxPropostaItem = {
  id: string;
  modelo_carregador: string;
  potencia_kw: number;
  quantidade: number;
  preco_equipamento: number;
  /** Id em `modelos_carregadores_veiculares`, quando a escolha veio do catálogo. */
  modelo_id?: string | null;
  imagem_url?: string | null;
};

function novoIdWallboxItem(): string {
  return typeof crypto !== "undefined" && typeof crypto.randomUUID === "function"
    ? crypto.randomUUID()
    : `wb-item-${Date.now()}-${Math.random().toString(36).slice(2, 9)}`;
}

export function criarItemWallboxProposta(
  modelo = "Wallbox 7kW",
  potenciaKw = 7,
  quantidade = 1,
  precoEquipamento = 0,
  modeloId: string | null = null,
  imagemUrl: string | null = null
): WallboxPropostaItem {
  return {
    id: novoIdWallboxItem(),
    modelo_carregador: modelo,
    potencia_kw: potenciaKw,
    quantidade: Math.max(1, quantidade),
    preco_equipamento: Math.max(0, precoEquipamento),
    modelo_id: modeloId,
    imagem_url: imagemUrl,
  };
}

export function itemPadraoWallboxProposta(): WallboxPropostaItem {
  return criarItemWallboxProposta("Wallbox 7kW", 7, 1, 0);
}

export function somaQuantidadeWallboxItens(itens: WallboxPropostaItem[]): number {
  return itens.reduce((acc, item) => acc + Math.max(1, Number(item.quantidade) || 1), 0);
}

export function somaEquipamentoWallboxItens(itens: WallboxPropostaItem[]): number {
  return itens.reduce((acc, item) => {
    const qtd = Math.max(1, Number(item.quantidade) || 1);
    return acc + (Number(item.preco_equipamento) || 0) * qtd;
  }, 0);
}

export function subtotalPropostaWallboxCompartilhado(
  itens: WallboxPropostaItem[],
  custos: {
    preco_instalacao: number;
    preco_materiais: number;
  },
  extrasTotal = 0
): number {
  const qtd = somaQuantidadeWallboxItens(itens);
  return (
    somaEquipamentoWallboxItens(itens) +
    ((Number(custos.preco_instalacao) || 0) + (Number(custos.preco_materiais) || 0)) * qtd +
    (Number(extrasTotal) || 0)
  );
}

export function rotuloModelosWallboxItens(itens: WallboxPropostaItem[]): string {
  if (!itens.length) return "—";
  return itens
    .map((item) => {
      const qtd = Math.max(1, Number(item.quantidade) || 1);
      return qtd > 1 ? `${item.modelo_carregador} (${qtd})` : item.modelo_carregador;
    })
    .join(" + ");
}

export function rotuloPotenciasWallboxItens(itens: WallboxPropostaItem[]): string {
  if (!itens.length) return "N/A";
  const partes: string[] = [];
  const vistos = new Set<string>();
  for (const item of itens) {
    const kw = Number(item.potencia_kw) || 0;
    const label = `${kw} kW`;
    if (vistos.has(label)) continue;
    vistos.add(label);
    partes.push(label);
  }
  return partes.join(" + ");
}

export function parseWallboxItensProposta(raw: unknown): WallboxPropostaItem[] {
  if (!Array.isArray(raw)) return [];
  return raw.map((row, index) => {
    const item = row && typeof row === "object" ? (row as Record<string, unknown>) : {};
    const id = typeof item.id === "string" && item.id ? item.id : `wb-item-${index}`;
    return {
      id,
      modelo_carregador:
        typeof item.modelo_carregador === "string" && item.modelo_carregador
          ? item.modelo_carregador
          : "Wallbox 7kW",
      potencia_kw: Math.max(0, Number(item.potencia_kw) || 0),
      quantidade: Math.max(1, Number(item.quantidade) || 1),
      preco_equipamento: Math.max(0, Number(item.preco_equipamento) || 0),
      modelo_id: typeof item.modelo_id === "string" && item.modelo_id ? item.modelo_id : null,
      imagem_url: typeof item.imagem_url === "string" && item.imagem_url ? item.imagem_url : null,
    };
  });
}

export function serializarWallboxItensProposta(itens: WallboxPropostaItem[]): WallboxPropostaItem[] {
  return itens.map((item) => ({
    id: item.id,
    modelo_carregador: item.modelo_carregador,
    potencia_kw: Number(item.potencia_kw) || 0,
    quantidade: Math.max(1, Number(item.quantidade) || 1),
    preco_equipamento: Math.max(0, Number(item.preco_equipamento) || 0),
    modelo_id: item.modelo_id || null,
    imagem_url: item.imagem_url || null,
  }));
}

export function agregarWallboxItensParaColunas(itens: WallboxPropostaItem[]): {
  modelo_carregador: string;
  potencia_kw: number;
  quantidade: number;
  preco_equipamento: number;
} {
  const primeiro = itens[0];
  if (!primeiro) {
    return {
      modelo_carregador: "Wallbox 7kW",
      potencia_kw: 7,
      quantidade: 1,
      preco_equipamento: 0,
    };
  }
  const modelosUnicos = [...new Set(itens.map((item) => item.modelo_carregador))];
  const potenciasUnicas = [...new Set(itens.map((item) => Number(item.potencia_kw) || 0))];
  return {
    modelo_carregador: modelosUnicos[0],
    potencia_kw: potenciasUnicas.length === 1 ? potenciasUnicas[0] : primeiro.potencia_kw,
    quantidade: somaQuantidadeWallboxItens(itens),
    preco_equipamento: Number(primeiro.preco_equipamento) || 0,
  };
}

const MODELO_SAVE_CONDOMINIOS = "Implantação de SAVE para Condomínios";

/** Propostas cujo modelo principal não é a lista de wallbox (SAVE, estudo EV, projeto). */
function identidadeVemDosItensWallbox(modelo: string | null | undefined): boolean {
  return (
    modelo !== MODELO_SAVE_CONDOMINIOS &&
    modelo !== "Estudo de Viabilidade - EV" &&
    modelo !== "Projeto e Aprovação de SAVE"
  );
}

function itensWallboxComEquipamento(raw: unknown): WallboxPropostaItem[] {
  return parseWallboxItensProposta(raw).filter(
    (item) => (Number(item.preco_equipamento) || 0) > 0
  );
}

export function rotuloModeloPropostaCarregador(proposta: {
  modelo_carregador?: string | null;
  wallbox_itens?: unknown;
}): string {
  if (!identidadeVemDosItensWallbox(proposta.modelo_carregador)) {
    const carregadores = itensWallboxComEquipamento(proposta.wallbox_itens);
    if (proposta.modelo_carregador === MODELO_SAVE_CONDOMINIOS && carregadores.length > 0) {
      return `${proposta.modelo_carregador} + ${rotuloModelosWallboxItens(carregadores)}`;
    }
    return proposta.modelo_carregador || "—";
  }
  const itens = parseWallboxItensProposta(proposta.wallbox_itens);
  if (itens.length > 0) return rotuloModelosWallboxItens(itens);
  return proposta.modelo_carregador || "—";
}

export function rotuloPotenciaPropostaCarregador(proposta: {
  modelo_carregador?: string | null;
  potencia_kw?: number | null;
  wallbox_itens?: unknown;
}): string {
  if (!identidadeVemDosItensWallbox(proposta.modelo_carregador)) {
    const carregadores = itensWallboxComEquipamento(proposta.wallbox_itens);
    if (carregadores.length > 0) return rotuloPotenciasWallboxItens(carregadores);
    const kw = Number(proposta.potencia_kw);
    return Number.isFinite(kw) && kw > 0 ? `${kw} kW` : "N/A";
  }
  const itens = parseWallboxItensProposta(proposta.wallbox_itens);
  if (itens.length > 0) return rotuloPotenciasWallboxItens(itens);
  const kw = Number(proposta.potencia_kw);
  return Number.isFinite(kw) && kw > 0 ? `${kw} kW` : "N/A";
}

export function quantidadePropostaCarregador(proposta: {
  modelo_carregador?: string | null;
  quantidade?: number | null;
  wallbox_itens?: unknown;
}): number {
  if (!identidadeVemDosItensWallbox(proposta.modelo_carregador)) {
    return Math.max(1, Number(proposta.quantidade) || 1);
  }
  const itens = parseWallboxItensProposta(proposta.wallbox_itens);
  if (itens.length > 0) return somaQuantidadeWallboxItens(itens);
  return Math.max(1, Number(proposta.quantidade) || 1);
}

export function proximoModeloWallboxDisponivel(
  itens: WallboxPropostaItem[],
  modelosDisponiveis: string[]
): string | null {
  const usados = new Set(itens.map((item) => item.modelo_carregador));
  const fallback = ["Wallbox 7kW", "Wallbox 22kW", "Wallbox 11kW"];
  const pool = [...(modelosDisponiveis.length ? modelosDisponiveis : fallback)];
  return pool.find((modelo) => !usados.has(modelo)) ?? null;
}

export function validarWallboxItensProposta(itens: WallboxPropostaItem[]): string | null {
  if (!itens.length) {
    return "Adicione ao menos um carregador na proposta.";
  }
  const modelos = new Set<string>();
  for (let i = 0; i < itens.length; i += 1) {
    const item = itens[i];
    if (!item.modelo_carregador) {
      return `Selecione o modelo do carregador ${i + 1}.`;
    }
    if (modelos.has(item.modelo_carregador)) {
      return `O modelo ${item.modelo_carregador} já foi adicionado. Altere a quantidade desse item.`;
    }
    modelos.add(item.modelo_carregador);
    if ((Number(item.quantidade) || 0) < 1) {
      return `Informe a quantidade do ${item.modelo_carregador} (mínimo 1).`;
    }
    if ((Number(item.potencia_kw) || 0) <= 0) {
      return `Informe a potência do ${item.modelo_carregador}.`;
    }
  }
  return null;
}
